import {Plugins, ElementPreset, Component} from '@packages/systems/wfdl';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import * as React from 'react';
import type {BreakpointsConfig} from '@packages/systems/style/types';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import type {
  EventsType,
  ActionListsType,
  InteractionsType,
} from '@packages/systems/ix2/types-core';
import type {
  RawStyleBlockServerType,
  SwatchType,
} from '@packages/systems/core/utils/StyleTypes';

import type {
  AnyVariableType,
  VariableCollection,
  VariableID,
} from '@packages/systems/core/utils/StyleVariables';
import {type RawFileType} from '@packages/systems/core/records/FileRecord';
import {
  LIBRARY_ACTION_SOURCE,
  LIBRARY_EXPORT_SUBTYPES,
  LIBRARY_EXPORT_TYPES,
  LIBRARY_PROFILE_STATUS,
  LIBRARY_SOURCE,
  LIBRARY_STATUS,
  LIBRARY_TRAITS,
} from './constants';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import type {RawStyleBlockType} from '@packages/systems/style-legacy/values';
import {RawCustomFontsType} from '@packages/systems/core/utils/FontUtils';
import {LocaleTag} from '@packages/systems/localization-types';
import {StylesMap} from '@packages/systems/style-legacy/utils';
import type {ObjectId} from 'bson';
import type {
  InteractionClient,
  TimelineClient,
} from '@packages/systems/ix3/schema';
import type {LibrarySourceFile} from '@packages/domains/instructions/data-types';

export type ComponentDependenciesMap = {
  [componentId: string]: ComponentDependencies;
};

export type LibraryMetadataShape = {
  developerSite: string;
  documentation: string;
  thumbnailImageId?: ObjectId | string;
};

export type LibraryResourceType = {
  manifest: {
    name: string;
    description: string;
    libraryMetadata: LibraryMetadataShape;
    siteId?: ObjectId | string;
    exports: Array<LibraryExport<ObjectId | string>>;
    traits?: Array<LibraryTraits>;
    libraryCompilerVersion?: number;
  };
  publisher?: ObjectId | string;
  siteVersion: number;
  contentUrl: string;
};

export type LibraryExportType =
  (typeof LIBRARY_EXPORT_TYPES)[keyof typeof LIBRARY_EXPORT_TYPES];

/**
 * Payload to pass to install library job
 */
export type InstallLibraryPayload = {
  taskId: string;
  siteId: string;
};

/**
 * Data to store when creating install library task
 */
export type InstallLibraryTaskData = {
  siteId: string;
  sharedResourceProfileId: string;
  versionedResourceId: string;
  installerId: string;
  componentsToDetach: string[];
  /**
   * Flag to indicate this is an admin install that should skip cross-workspace validation
   */
  isAdminInstall?: boolean;
  /**
   * Flag to indicate this is a push update
   */
  isPushUpdate?: boolean;
  /**
   * Whether the library's tag styles should be applied on install. The caller
   * decides — the Designer asks the user when the site already has tag styles
   * to override — so an absent flag means "leave them switched off".
   */
  shouldActivateTagStyles?: boolean;
  /**
   * Where the install or push came from, for analytics. Absent means the
   * Designer.
   */
  source?: LIBRARY_ACTION_SOURCE;
};

export type UninstallLibraryPayload = InstallLibraryPayload;
export type UninstallLibraryTaskData = {
  siteId: string;
  sharedResourceProfileId: string;
  userId: string;
  source?: LIBRARY_ACTION_SOURCE;
};

/**
 * Payload to pass to recover orphaned library content job
 */
export type RecoverOrphanedLibraryContentPayload = {
  taskId: string;
  siteId: string;
  userId: string;
  branchId?: string;
};

/**
 * A library export for a layout. Sometimes referred to as a "pre-built layout"
 * in our codebase.
 */
export type LayoutExport<IDType> = {
  type: typeof LIBRARY_EXPORT_TYPES.LAYOUT;
  layoutMetadata: LayoutMetadata<IDType>;
};

/**
 * A library export for a component.
 */
export type ComponentExport = {
  type: typeof LIBRARY_EXPORT_TYPES.COMPONENT;
  subtype?:
    | LIBRARY_EXPORT_SUBTYPES.COMPONENT
    | LIBRARY_EXPORT_SUBTYPES.CODE_COMPONENT;
  metadata: ComponentMetadata;
};

/**
 * A library export for a variable.
 */
export type VariableExport = {
  type: typeof LIBRARY_EXPORT_TYPES.VARIABLE;
  metadata: VariableMetadata;
};

/*
 * A library export for an asset.
 */
export type AssetExport = {
  type: typeof LIBRARY_EXPORT_TYPES.ASSET;
  metadata: AssetMetadata;
};

/**
 * A library export for a tag style (root tag StyleBlock).
 */
export type TagStyleExport = {
  type: typeof LIBRARY_EXPORT_TYPES.TAG_STYLE;
  metadata: TagStyleMetadata;
};

/**
 * Metadata associated with a font carried by a library.
 *
 * The persisted shape is the thin pointer `{targetId, displayName}`.
 * `weights` is a wire-only display field stamped at hydrate time and
 * stripped before persistence; the emitter does not set it.
 */
export type FontMetadata = {
  /**
   * The stable id of the font — the user-visible family name, shared by
   * every source (Google, custom files, Adobe Typekit) providing the
   * family. Matches `origin[1]` on the consumer site after install.
   */
  targetId: string;
  /**
   * The user-facing name of the font (family name). Persisted on the export
   * so removed-entry changelog rows can render without dereferencing the
   * canonical store (parity with the other export types' `displayName`).
   */
  displayName: string;
  /**
   * Display-only weights for the changelog row, stamped at hydrate time.
   * Not persisted on the subdocument. Empty for Adobe (no per-kit weight
   * display).
   */
  weights?: string[];
  /**
   * Whether this font family is provided by an Adobe Typekit kit. Display-only,
   * stamped at hydrate time like `weights`; set only when true and not
   * persisted on the subdocument.
   */
  isAdobeTypekit?: boolean;
};

/**
 * A library export for a font.
 */
export type FontExport = {
  type: typeof LIBRARY_EXPORT_TYPES.FONT;
  metadata: FontMetadata;
};

export type SourceFileExport = {
  type: typeof LIBRARY_EXPORT_TYPES.SOURCE_FILE;
  /** Instruction family this export belongs to: a skill root or a rule. Companion files are not exported. */
  subtype: LIBRARY_EXPORT_SUBTYPES.SKILL | LIBRARY_EXPORT_SUBTYPES.RULE;
  metadata: {
    /** The stable source file id. */
    targetId: string;
    /** Full instruction path, for deterministic display and counting. */
    displayName: string;
  };
};

/**
 * Metadata associated with a layout.
 */
export type LayoutMetadata<IDType> = {
  /**
   * The component ID that contains the layout contents.
   */
  targetComponentId: string;
  /**
   * The user-facing name of the layout.
   */
  displayName: string;
  /**
   * The category or path of folders that the layout is in.
   */
  category: Array<string>;
  /**
   * An asset that provides a preview of the layout.
   */
  previewImage: {
    /**
     * A URL of an image to use as a preview.
     */
    url: string;
    /**
     * An asset ID.
     */
    id: IDType;
  };
};

/**
 * Metadata associated with a component.
 */
export type ComponentMetadata = {
  /**
   * The component ID.
   */
  targetComponentId: string;
  /**
   * The user-facing name of the component.
   */
  displayName: string;
  /**
   * A list of all the components nested somewhere in this component Tree (N levels)
   */
  nestedComponents?: Array<Component.ComponentName>;
  /**
   * Indicates whether this component is localized or not
   */
  localized?: boolean;
};

export type PluginCompilationComponentData = {
  /**
   * A map of componentIds to their dependencies
   */
  componentDependencies: ComponentDependenciesMap;
  styleVariables: Plugins.PluginType['styleVariables'];
  styleVariableCollections: Plugins.PluginType['styleVariableCollections'];
  styleBlocks: Plugins.PluginType['styleBlocks'];
  assets: Plugins.PluginType['assets'];
  /**
   * Component map reified from library symbols nodes
   */
  initialComponents: Component.ComponentMap;
  /**
   * Updated component map where nested component instances
   * are updated from __SitePlugin::componentId to libraryId::componentId
   */
  updatedComponents: Component.ComponentMap;
  nestedComponentDependencyMap: ComponentDependencyMap;
};

export type VariableMetadata = {
  /**
   * The variable ID.
   */
  targetVariableId: string;
  /**
   * The name of the variable.
   */
  displayName: string;
};

export type AssetMetadata = {
  /**
   * The asset ID.
   */
  targetId: string;
  /**
   * The name of the asset.
   */
  displayName: string;
};

export type TagStyleMetadata = {
  /**
   * The StyleBlock ID this tag style targets (root tag selector).
   */
  targetStyleBlockId: string;
  /**
   * The user-facing name (e.g. "All H1s").
   */
  displayName: string;
};

/**
 * A general library export.
 */
export type LibraryExport<IDType> =
  | LayoutExport<IDType>
  | ComponentExport
  | VariableExport
  | AssetExport
  | TagStyleExport
  | FontExport
  | SourceFileExport;

type LibraryExportHashes = {
  /**
   * The hash of the component, variable, or variable collection content: render + dependencies (style variables, style blocks, assets, interactions)
   */
  contentsHash?: string;
  /**
   * The hash of the component, variable, or variable collection metadata: displayName, description and groupName
   */
  metadataHash?: string;
  /**
   * A list of hashes of each component prop
   */
  propsHash?: PropsHashType;
};

export type LibraryExportWithHashes<
  IDType,
  LibraryExportT = LibraryExport<IDType>,
> = LibraryExportT &
  (LibraryExportT extends ComponentExport
    ? {metadata: LibraryExportHashes & ComponentExport['metadata']}
    : LibraryExportT extends VariableExport
      ? {metadata: LibraryExportHashes & VariableExport['metadata']}
      : LibraryExportT extends AssetExport
        ? {metadata: LibraryExportHashes & AssetExport['metadata']}
        : LibraryExportT extends TagStyleExport
          ? {metadata: LibraryExportHashes & TagStyleExport['metadata']}
          : LibraryExportT extends FontExport
            ? {metadata: LibraryExportHashes & FontExport['metadata']}
            : LibraryExportT extends SourceFileExport
              ? {metadata: LibraryExportHashes & SourceFileExport['metadata']}
              : LibraryExportT);

/**
 * A general verified library export.
 */
export type VerifiedLibraryExport<
  IDType,
  LibraryExportT = LibraryExport<IDType>,
> = LibraryExportWithHashes<IDType, LibraryExportT> & {
  /**
   * Indicates whether this LibraryExport is a valid export or not. If false,
   * this export should not be processed during library publish
   */
  valid: boolean;

  /**
   * The errors identified during verification of this LibraryExport
   */
  errors?: ErrorResult[];
};

/**
 * The types of known errors that we don't allow in a library.
 */
export type TraversalError =
  | {
      type: 'FORBIDDEN_ELEMENT';
      element: ExpressionElement;
    }
  | {
      type: 'LEGACY_INTERACTION';
      element: ExpressionElement;
    }
  | {
      type: 'FORBIDDEN_BINDING';
    }
  | {
      type: 'FORBIDDEN_PROPERTY';
      label: string;
    }
  | {
      type: 'FORBIDDEN_SLOT_DEFAULT';
    }
  | {
      type: 'DANGLING_SLOT_DEFAULT_REFERENCE';
      elementId: string;
      slotLabel: string;
    }
  | {
      type: 'CROSS_LIBRARY_COMPONENT';
    };

export type TraversalErrorType = TraversalError['type'];

export type ErrorResult = {
  message: string;
  elementId?: string;
  elementLabel?: string;
  type: TraversalErrorType;
};

/**
 * The source site of a library
 */
export type LibrarySourceSite = {
  archived: boolean;
  shortName: string;
  workspaceId: string;
};

/**
 * Traits that can be associated with a library.
 */
export type LibraryTraits = keyof typeof LIBRARY_TRAITS;

/**
 * The frontend representation of a library.
 *
 * TODO: Document/associate this with backend data structures in a more formal
 * way. For instance, we could treat this data as "shared" and the backend could
 * extend this shape with additional data as needed.
 */
export type LibraryManifest<IDType> = {
  /**
   * The Versioned Resource ID for this library
   * which is used as Plugin ID
   */
  pluginId: string;
  /**
   * The Shared Resource Profile ID for this library
   */
  profileId: string;
  /**
   * The profile id of the library this local override stands in for. Not
   * `profileId`: that is a hash of the override's own plugin id, and reusing the
   * real one would make lookups by `profileId` match both libraries.
   */
  overridesProfileId?: string;
  /**
   * The user-facing name of the library.
   */
  displayName: string;
  /**
   * The user-facing icon to display next to the name.
   */
  displayIcon?: React.ComponentType;
  /**
   * The user-facing version of the installed library.
   */
  version: string;
  /**
   * The user-facing installer of the library.
   */
  installer?: string;
  /**
   * Whether the library is served from a developer's own machine rather than
   * published.
   */
  isLocalOverride?: boolean;
  /**
   * The publishing user of the library.
   */
  publisher?: string;
  /**
   * The profile image of the publishing user of the library.
   */
  publisherImg?: string;
  /**
   * Indicates if the library is external to Webflow
   */
  isExternal: boolean;
  /**
   * The things this library contains.
   */
  exports: Array<LibraryExport<IDType>>;
  /**
   * The count of how many components this library contains.
   */
  componentsCount: number;

  /**
   * The count of how many code components this library contains.
   */
  codeComponentsCount: number;

  /**
   * The count of how many variables this library contains.
   */
  variablesCount: number;

  /**
   * The count of how many assets this library contains.
   */
  assetsCount: number;

  /**
   * The count of how many tag styles this library contains.
   */
  tagStylesCount: number;

  /**
   * The count of how many fonts this library contains.
   */
  fontsCount: number;

  /** The count of agent instruction skill/rule files captured from the source site. */
  sourceFilesCount?: number;

  /** The count of agent instruction skill files captured from the source site. */
  skillsCount?: number;

  /** The count of agent instruction rule files captured from the source site. */
  rulesCount?: number;

  /**
   * The type of the library
   */
  type: 'LIBRARY' | 'CODE_LIBRARY';

  /** The current status of the library as it exists in the Designer.
   */
  status: LIBRARY_STATUS;

  /** The current status of the library profile: shared or unshared
   */
  profileStatus: LIBRARY_PROFILE_STATUS;

  /**
   * Library source: Workspace or Marketplace
   */
  source: LIBRARY_SOURCE;

  /**
   * Library source site
   */
  sourceSite?: LibrarySourceSite;

  /** The manifest of the corresponding library version update for this library */
  updatedLibraryVersion?: LibraryManifest<string>;

  /** The date this library version was created */
  datePublished: string | null;

  /** Description for this library version  */
  description: string | null;

  /** Traits additional traits like themeable and ai_site_gen */
  traits?: Array<LibraryTraits>;

  /** The version of the library compiler used to compile this library */
  libraryCompilerVersion?: number;

  /**
   * Settings associated with the library profile
   */
  librarySettings?: LibrarySettings | null;
};

export type Ix2Type = {
  interactions: InteractionsType;
  events: EventsType;
  actionLists: ActionListsType;
};

export type Ix3Type = {
  interactions: InteractionClient[];
  timelines: TimelineClient[];
};

export type SymbolsLocalizationData = Record<
  LocaleTag,
  {[nodeId: string]: JSONNode}
>;

export type LibraryContent = {
  symbols: Array<JSONNode>;
  styles: {
    data: {
      swatches: SwatchType[];
      macros: Array<unknown>;
      breakpoints: BreakpointsConfig;
      migrations: {
        stylesNext: boolean;
      };
      appliedStylesMap: StylesMap;
    };
    blocks: Array<RawStyleBlockServerType>;
  };
  assets: Array<RawFileType>;
  ix2: Ix2Type;
  ix3?: Ix3Type;
  variables?: Array<AnyVariableType>;
  variableCollections?: Array<VariableCollection> | null;
  customFonts?: RawCustomFontsType;
  symbolsLocalizations?: SymbolsLocalizationData;
  /** Agent instruction skill/rule files captured from the source site. */
  sourceFiles?: LibrarySourceFile[];
};

export type AssetMap = {
  [key: string]: RawFileType;
};

export type ComponentDependencyMap = {
  [key: Component.ComponentName]: Component.ComponentName[];
};

export type ComponentDependencies = {
  interactions: ElementPreset.ElementPresetInteractions;
  ix3?: Ix3Type;
  assetsIds: NonNullable<ElementPreset.ElementPresetType['assetsIds']>;
  styleBlocks: RawStyleBlockType[];
  swatches: SwatchType[];
  styleVariableIds: Array<Qualified<VariableID>>;
  symbolsLocalizations?: SymbolsLocalizationData;
};

export type PropsHashType = {
  [key: string]: string;
};

export type LibraryExportHash = {
  /**
   * A hash of the raw content of this item.
   *
   * When comparing hashes, a change in this hash
   * will flag the item to be "changed".
   *
   * For Components only, a change in this hash will flag all
   * components with instances of the changed component
   * also as "changed".
   *
   * For Components, this includes:
   * - the component render
   * - the component style variants
   * - the component "dependencies" from the element preset
   *   (style blocks, ix2, assets, variables) so that changing
   *   any of those items used in the component would also
   *   change the component's contentsHash
   * - the contentsHash of all nested components
   *
   * For Variables, this includes:
   * - the variable value
   *
   * For Assets, this includes:
   * - the asset fileHash
   */
  contentsHash: string;
  /**
   * A hash of the metadata of this item.
   *
   * When comparing hashes, a change in this hash
   * will flag the item to be "changed".
   *
   * For Components, this includes:
   * - name
   * - description
   * - groupName
   *
   * For Variables, this includes:
   * - name
   * - order
   *
   * For Assets, this includes:
   * - file name
   * - alt text
   * - folder
   */
  metadataHash?: string;
  /**
   * (Components only) A hash map of each Component prop.
   *
   * When comparing hashes
   * - a change in a prop hash will flag the component to be "changed"
   * - a prop that was deleted will flag the component to be "possible conflict"
   */
  propsHash?: PropsHashType;
};
/**
 * A map of each Library Export Item to its relevant hashes.
 * This is used to compare different versions of a library
 * and generate a changelog.
 */
export type LibraryExportsHashMap = {[key: string]: LibraryExportHash};

export type PluginCompilationMetadata = {
  dependencyMap: ComponentDependencyMap;
  hashMap: LibraryExportsHashMap;
  componentDependencies: ComponentDependenciesMap;
  /**
   * Optional `targetId → {weights}` map, built from
   * `LibraryContent.customFonts` so the hydrate step can stamp a FONT
   * entry's wire-only `weights` without re-reading customFonts. Not
   * persisted on the LibraryExport subdocument.
   */
  displayMap?: {
    [targetId: string]: {weights: string[]; isAdobeTypekit?: boolean};
  };
};

export type LibrarySummary = {
  id: string;
  profileId: string;
  profileStatus: LIBRARY_PROFILE_STATUS;
  type: 'LIBRARY' | 'CODE_LIBRARY';
  displayName: string;
  version: string;
  description?: string;
  datePublished?: string;
  componentsCount?: number;
  codeComponentsCount?: number;
  variablesCount?: number;
  assetsCount?: number;
  tagStylesCount?: number;
  fontsCount?: number;
  sourceFilesCount?: number;
  skillsCount?: number;
  rulesCount?: number;
  installationCount?: number;
  outdatedInstallationCount?: number;
  publisher?: {
    name: string;
    profileImg?: string;
  };
  thumbnailImageId?: string;
  sourceSite?: LibrarySourceSite;
  source: LIBRARY_SOURCE;
  traits?: Array<LibraryTraits>;
  libraryCompilerVersion?: number;
  librarySettings?: LibrarySettings | null;
};

export type LibraryInstallationSummary = {
  id: string;
  installedSite: {
    id: string;
    archived: boolean;
    displayName: string;
    shortName: string;
    workspaceId: string;
    previewImgUrl?: string | null;
  };
  libraryVersion: {
    id: string;
    version: string;
    profileId: string;
  };
  installer: {
    id: string;
    profileImg?: string | null;
    firstName?: string;
    lastName?: string;
  };
  dateInstalled: string;
  hasInFlightTask?: boolean;
};

export type LibraryVersionedResource = Omit<LibrarySummary, 'publisher'> & {
  installer?: string;
  /** Set when the library is served from a developer's own machine, not published. */
  isLocalOverride?: boolean;
  /** Set on a local override: the installed library's profile id it stands in for. */
  overridesProfileId?: string;
  exports: LibraryExport<string>[];
  publisher?: string;
  publisherImg?: string;
  plugin:
    | {
        type: 'URL' | 'DEFERRED_URL';
        value: string;
      }
    | {
        type: 'CONTENT';
        value: Plugins.PluginDeclarations;
      };
};

export type LibraryVersionedResourceResult =
  | {
      status: 'OK';
      value: LibraryVersionedResource;
    }
  | {
      status: 'FAILED';
      value: {
        id: string;
        error: string;
      };
    };

/**
 * Metadata indicating what library update was performed on main:
 * - First install: {mainResourceId: 'library-id', branchResourceId: null}
 * - Update: {mainResourceId: 'library-id-v2', branchResourceId: 'library-id-v1'}
 * - Uninstall: {mainResourceId: null, branchResourceId: 'library-id'}
 */
type BranchLibraryUpdateMeta = {
  /**
   * Library VersionedResource id on main
   */
  mainResourceId: string | null;
  /**
   * Library VersionedResource id on branch
   */
  branchResourceId: string | null;
};

/**
 * A type defining the library updates metadata on a page branch.
 */
export type BranchLibrariesUpdates = {
  updates: {
    count: number;
    all: Array<BranchLibraryUpdateMeta>;
  };
};

/** A type defining the generated map of a library export item
 * and the state it was determined to be in between one version of
 * the library and an updated version of the library
 *
 * Typically, the `libraryExport` defined will be the library export of
 * the prior version of the library, since in an end-user's point-of-view
 * they are currently utilizing and referenceing the prior one.
 *
 * The updated library version of an export is used typically for state
 * when an export has determined to be `added` since it follows that the
 * added library export would not exist in a prior version of the library
 */
type DiffResultItemUpdatedStates =
  | LibraryExportDiffState.changed
  | LibraryExportDiffState.conflict;

export type DiffResultItemExportType<T = LibraryExport<string>> =
  | {
      state: Exclude<LibraryExportDiffState, DiffResultItemUpdatedStates>;
      libraryExport: T;
    }
  | {
      state: DiffResultItemUpdatedStates;
      libraryExport: T;
      updatedLibraryExport: T;
    };

export type DiffResultType = {
  [libraryExportId: string]: DiffResultItemExportType;
};

/** The different states that can be determined for a library export between a
 *  version of a library and an updated version of that same library
 */
export enum LibraryExportDiffState {
  conflict = 'conflict',
  changed = 'changed',
  unchanged = 'unchanged',
  added = 'added',
  removed = 'removed',
}

export type LibrariesModalState<T extends LibraryModalType = LibraryModalType> =
  {
    open: boolean;
    modal: T | null;
    modalData: T extends keyof LibraryModalTypeData
      ? LibraryModalTypeData[T]
      : null;
  };

/**
 * Maps modal types to their expected data payload.
 */
export type LibraryModalTypeData = {
  /**
   * Data shown when reviewing a library update.
   */
  'review-library': {
    /** ID of the library profile to update. */
    profileId: string;
  };

  /**
   * Data shown when uninstalling a library.
   */
  'uninstall-library': {
    /** ID of the library profile to uninstall. */
    profileId: string;
  };

  /**
   * Data shown when prompting the user to apply tag styles before
   * installing a library that ships tag styles.
   */
  'apply-tag-styles': {
    /** ID of the library profile being installed. */
    profileId: string;
    /** Version (plugin) ID of the library being installed. */
    versionId: string;
  };

  /**
   * Data shown after library updates are applied.
   */
  'library-updates-applied': {
    /** ID of the library profile. */
    profileId: string;
    /** Resource ID of the previous library version. */
    priorLibraryResourceId: string;
    /** Resource ID of the updated library version. */
    updatedLibraryResourceId: string;
    /** Display name of the library. */
    displayName: string;
    /**
     * Username of the person who installed the update.
     * If it's the same as the current user, show "You".
     */
    installerUsername: string;
    /** Display name of whoever installed the update. */
    installerDisplayName: string;
    /** Optional installer image URL. */
    installerProfileImg?: string;
    /** Date when the update was installed. */
    dateInstalled: string;
  };
};

/**
 * All types of modals managed by LibraryModalContainer
 */
export type LibraryModalType =
  | 'disable-cloning'
  | 'share-library'
  | 'unshare-library'
  | 'review-library'
  | 'uninstall-library'
  | 'asset-settings'
  | 'library-installations'
  | 'installing-library'
  | 'library-updates-applied'
  | 'apply-tag-styles';

export type LibraryAssetManagementMap = {
  [assetId: string]:
    | {
        /**
         * Used when we can't find any matching assets on site data that
         * we can reuse so we need to import a new asset.
         *
         * Example: Brand new asset identified during library update/install.
         */
        import: true;
        value: RawFileType;
      }
    | {
        /**
         * Used during manual detach operations.
         *
         * Example: User manually detaches a component or asset from the library.
         */
        duplicate: true;
        value: RawFileType;
      }
    | {
        /**
         * Used when the asset is not going to be used anymore.
         *
         * Example: Asset is not used by any component anymore, so during library
         * update we must delete it.
         */
        delete: true;
        value: {
          _id: RawFileType['_id'];
        };
      }
    | {
        /**
         * Used when we can find some compatible asset on site data that has
         * the same fileHash and comparable origin.
         *
         * Example: When detaching a component from library, there could already
         * be an asset on site data that we can use, so we don't need to import it.
         */
        reuse: true;
        value: {
          _id: RawFileType['_id'];
        };
      }
    | {
        /**
         * Used when it is safe to just update the asset origin during library
         * update/uninstall.
         *
         * Examples:
         * 1) When updating library version, we can just update the origin of the asset.
         * 2) When uninstalling a library, we can just 'remove' the origin of the asset.
         */
        update: true;
        value: RawFileType & {
          origin: RawFileType['origin'] | null;
        };
      };
};

export type FetchChangelogActionInput = 'COLLECT' | 'QUERY';

export type FetchChangelogResourceIdInput = [string, string?];

export type FetchChangelogOptions = {
  patchAssetIds?: boolean;
  libraryProfileId?: string;
  /**
   * When true, the export pipeline includes agent instruction SourceFile
   * entries and library content collection includes their sourceFiles.
   */
  isAgentInstructionsEnabled?: boolean;
};

export enum FETCH_CHANGELOG_STATUS {
  PENDING = 'PENDING',
  FINISHED = 'FINISHED',
  FAILED = 'FAILED',
}

type FetchChangelogPendingPayload = {
  status: FETCH_CHANGELOG_STATUS.PENDING;
};

export type FetchChangelogFinishedPayload = {
  status: FETCH_CHANGELOG_STATUS.FINISHED;
  data: {
    [key: string]: VerifiedLibraryExport<string>[];
  };
};

type FetchChangelogFailedPayload = {
  status: FETCH_CHANGELOG_STATUS.FAILED;
};

export type FetchChangelogPayload =
  | FetchChangelogPendingPayload
  | FetchChangelogFinishedPayload
  | FetchChangelogFailedPayload;

export type LoadedLibraries = {
  [pluginId: string]: LibraryManifest<string>;
};

export type LibrariesInstallationType = {
  [libraryPluginId: string]: {
    type: 'LIBRARY' | 'CODE_LIBRARY';
    name: string;
    installationStatus:
      | LIBRARY_STATUS.installing
      | LIBRARY_STATUS.installed
      | LIBRARY_STATUS.uninstalling;
    profileId: string;
  };
};

// Used for providing necessary context to determien the capabilities of a library
// currently it's just loadedLibraries, but we could eventually supply additional context
// such as apps, etc.
export interface LibraryCapabilitiesContext {
  loadedLibraries: LoadedLibraries;
}

export type LibraryAssetCategories =
  | 'image'
  | 'document'
  | 'lottie'
  | 'rive'
  | 'audio';
export type LibraryExportCategories =
  | 'component'
  | 'code-component'
  | 'variable'
  | 'tag-style'
  | 'font'
  | LibraryAssetCategories;

export type LibrarySettings = {
  assetFoldersIgnored?: (ObjectId | string | 'NO_FOLDER')[];
  defaultSiteInstallation?: boolean;
};
